import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import PrincipleQuestions from "./PrincipleQuestions";

const SinglePrinciple = ({ item, setSelectedItem }) => {
  const [questionsData, setQuestionsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiCallForQuestions, setApiCallForQuestions] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [principleData, setPrincipleData] = useState([]);
  const [allPrinciplesData, setAllPrinciplesData] = useState([]); 
  const principles = [
    { id: 5, title: "Principio 1" },
    { id: 6, title: "Principio 2" },
    { id: 7, title: "Principio 3" },
    { id: 8, title: "Principio 4" },
    { id: 9, title: "Principio 5" },
    { id: 12, title: "Principio 6" },
    { id: 10, title: "Principio 7" },
    { id: 11, title: "Principio 8" },
    { id: 13, title: "Principio 9" },
    { id: 14, title: "Principio 10" },
    { id: 15, title: "Principio 11" },
  ];
  useEffect(() => {
    if (item?.id && setSelectedItem) {
      setSelectedItem(item.title);
    }
  }, [item, setSelectedItem]);
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/principal/`,
          {
            headers: {
              Authorization: `Token ${localStorage.getItem("auth_token")}`,
            },
          }
        );
        setAllPrinciplesData(response?.data?.data);
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusColor = (questions = []) => {
    if (!questions.length) return "#D3D3D3";

    const answerStatuses = questions.map((q) => {
      if (!q || !q.answer) return false;

      const isYesNo = q.question_type === "SN";
      const isMC = q.question_type === "OM";

      const hasYesNo = isYesNo && typeof q.answer.yes_no_answer === "string";
      const hasChoices =
        isMC && Array.isArray(q.answer.choices) && q.answer.choices.length > 0;

      return hasYesNo || hasChoices;
    });

    const allFilled = answerStatuses.every(Boolean);
    const allEmpty = answerStatuses.every((status) => status === false);

    if (allFilled) return "#9ACD32";
    if (allEmpty) return "#D3D3D3";
    return "#FFD700";
  };

  // Match principles with API data and calculate colors
  const principlesWithColors = useMemo(() => {
    return allPrinciplesData.map((principle) => {
      const matchedData = principleData.find(
        (data) => data.id === principle.id
      );
      const color = getStatusColor(matchedData?.questions || []);
      return {
        ...principle,
        color,
        questions: matchedData?.questions || [],
      };
    });
  }, [principles, principleData]);

  // Modify existing useEffect
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // 1. Fetch questions for current principle
        const questionsResponse = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/principal/${item?.id}/questions/`,
          {
            headers: {
              Authorization: `Token ${localStorage.getItem("auth_token")}`,
            },
          }
        );
        setQuestionsData(questionsResponse?.data?.data);

        // 2. Refresh all principles data
        const principlesResponse = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/principal/`,
          {
            headers: {
              Authorization: `Token ${localStorage.getItem("auth_token")}`,
            },
          }
        );
        setPrincipleData(principlesResponse?.data?.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (item?.id) {
      fetchData();
    }
  }, [item?.id, refreshTrigger]); // Add refreshTrigger here

  const handleAnswerSubmitted = () => {
    setRefreshTrigger((prev) => !prev); // Toggle to trigger refresh
  };
  return (
    <div className="main-container">
      <div className="container-header">
        <div
          className="left-content"
          style={{
            flex: "0 0 40%",
            display: "flex",
            alignItems: "center",
            // paddingLeft: "60px",
          }}
        >
          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(8, 1fr)",
                gap: "10px",
                padding: "20px",
                justifyItems: "center",
              }}
            >
              {principlesWithColors.map((principle) => {
                const isSelected = item?.id === principle.id;

                return (
                  <Box
                    key={principle.id}
                    sx={{
                      width: {
                        lg: "calc(12.5% - 10px)", // 100 / 8 = 12.5% per item
                        md: "calc(25% - 10px)",
                        sm: "calc(33.33% - 10px)",
                        xs: "calc(50% - 10px)",
                      },
                      minWidth: "120px",
                      border: "1px solid #ccc",
                      borderRadius: "5px",
                      textAlign: "center",
                      fontFamily: "Arial, sans-serif",
                      position: "relative",
                      padding: "5px 10px 18px 10px",
                      cursor: "pointer",
                      backgroundColor: "white",
                      boxShadow: isSelected
                        ? "0px 4px 10px rgba(0, 0, 0, 0.8)"
                        : "none",
                      transition: "all 0.3s ease-in-out",
                    }}
                    onClick={() =>
                      setSelectedItem && setSelectedItem(principle.title)
                    }
                  >
                    Principio {principle.sorting_index}
                    <div
                      style={{
                        position: "absolute",
                        bottom: "0",
                        left: "0",
                        width: "100%",
                        height: "15px",
                        backgroundColor: principle.color,
                        borderRadius: "0 0 5px 5px",
                      }}
                    ></div>
                  </Box>
                );
              })}
            </Box>
          )}
        </div>
      </div>

      {/* Render the PrincipleQuestions component based on the selected item */}
      {item && (
        <div className="comment-section-body">
          <PrincipleQuestions
            questions={questionsData}
            principle={principleData?.find((data) => data?.id === item?.id)}
            isLoading={isLoading}
            onAnswerSubmitted={handleAnswerSubmitted}
          />
        </div>
      )}
    </div>
  );
};

export default SinglePrinciple;
